import { buildApiUrl } from '../config/env';
import { readResponseJson } from '../lib/api/http';
import { storage } from './storage';
import * as PhoneAuth from '../services/phoneAuth';

const EXPIRY_SKEW_MS = 5 * 60 * 1000;

let refreshInFlight: Promise<string | null> | null = null;

const decodeJwtPayload = (token: string): { exp?: number } | null => {
  try {
    const payload = token.split('.')[1];
    if (!payload) {
      return null;
    }
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const atobFn = globalThis.atob;
    if (!atobFn) {
      return null;
    }
    return JSON.parse(atobFn(padded));
  } catch (error) {
    console.warn('⚠️ Failed to decode auth token payload:', error);
    return null;
  }
};

const isTokenExpiredOrExpiring = (token: string, skewMs = EXPIRY_SKEW_MS): boolean => {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) {
    return false;
  }
  return payload.exp * 1000 <= Date.now() + skewMs;
};

const exchangeFirebaseTokenForBackendJwt = async (
  firebaseToken: string,
  phoneNumber: string
): Promise<string | null> => {
  const refreshResponse = await fetch(buildApiUrl('api/auth/refresh'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ firebaseToken }),
  });

  if (refreshResponse.ok) {
    const refreshData = await readResponseJson(refreshResponse);
    if (refreshData?.success && refreshData.token) {
      return refreshData.token;
    }
  }

  // Existing backends may not have /refresh yet. Reuse verify-otp, which already
  // issues a backend JWT from a valid Firebase ID token.
  const fallbackResponse = await fetch(buildApiUrl('api/auth/verify-otp'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      phone: phoneNumber,
      firebaseToken,
    }),
  });

  if (!fallbackResponse.ok) {
    return null;
  }

  const fallbackData = await readResponseJson(fallbackResponse);
  if (fallbackData?.success && fallbackData.token) {
    return fallbackData.token;
  }

  return null;
};

export const refreshBackendSession = async (): Promise<string | null> => {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = (async () => {
    try {
      await PhoneAuth.waitForAuthReady();
      const firebaseUser = PhoneAuth.getCurrentUser();
      const phoneNumber = firebaseUser?.phoneNumber;
      if (!firebaseUser || !phoneNumber) {
        console.warn('🔒 Cannot refresh session: no Firebase user');
        return null;
      }

      const firebaseToken = await PhoneAuth.getIdToken(true);
      if (!firebaseToken) {
        console.warn('🔒 Cannot refresh session: no Firebase ID token');
        return null;
      }

      const backendToken = await exchangeFirebaseTokenForBackendJwt(firebaseToken, phoneNumber);
      if (!backendToken) {
        console.warn('🔒 Backend session refresh failed');
        return null;
      }

      await storage.saveToken(backendToken);
      console.log('✅ Backend session refreshed from Firebase user');
      return backendToken;
    } catch (error) {
      console.error('❌ Error refreshing backend session:', error);
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
};

export const getValidAuthToken = async (): Promise<string | null> => {
  const storedToken = await storage.getStoredToken();
  if (storedToken && !isTokenExpiredOrExpiring(storedToken)) {
    return storedToken;
  }

  const refreshedToken = await refreshBackendSession();
  if (refreshedToken) {
    return refreshedToken;
  }

  if (storedToken && !isTokenExpiredOrExpiring(storedToken, 0)) {
    return storedToken;
  }

  return null;
};
